import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Grid,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Divider,
  Card,
  CardContent,
  Tab,
  Tabs,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Chip,
  Stack,
  IconButton,
  Tooltip,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import RefreshIcon from "@mui/icons-material/Refresh";
import DownloadIcon from "@mui/icons-material/Download";
import ChatTable from "./dashboard/ChatTable";
import MessagesView from "./dashboard/MessagesView";
import AnalyticsCards from "./dashboard/AnalyticsCards";
import SentimentChart from "./dashboard/SentimentChart";
import AIMetricsCard from "./dashboard/AIMetricsCard";
import RealtimeStats from "./dashboard/RealtimeStats";
import AdvancedAnalytics from "./dashboard/AdvancedAnalytics";
import UserLogs from "./dashboard/UserLogs";
import FollowUpMonitor from "./dashboard/FollowUpMonitor";
import IntentMonitor from "./dashboard/IntentMonitor";
import IntentSummaryCard from "./dashboard/IntentSummaryCard";
import LanguageSelector from "./LanguageSelector";
import { analyticsApi } from "../services/api";
import KnowledgeBaseSettings from "./KnowledgeBaseSettings";
import UserSettingsDropdown from "./UserSettingsDropdown";
import { getCurrentUser } from "../services/user";

function Dashboard() {
  const { t } = useTranslation();
  const [analytics, setAnalytics] = useState(null);
  const [sentimentData, setSentimentData] = useState(null);
  const [aiMetrics, setAiMetrics] = useState(null);
  const [realtimeStats, setRealtimeStats] = useState(null);
  const [userEngagement, setUserEngagement] = useState(null);
  const [intentStats, setIntentStats] = useState([]);
  const [chatLogs, setChatLogs] = useState({ logs: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [showMessagesView, setShowMessagesView] = useState(false);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [filters, setFilters] = useState({
    platform: "",
    days: 30,
    startDate: null,
    endDate: null,
  });
  const [chatFilters, setChatFilters] = useState({
    page: 0,
    pageSize: 10,
    query: "",
  });

  useEffect(() => {
    fetchAllAnalytics();
  }, [filters]);

  // Get current user information for role-based features
  useEffect(() => {
    const loadUserInfo = async () => {
      try {
        const user = await getCurrentUser();
        setCurrentUser(user);
        setIsAdmin(user && user.role === 'admin');
      } catch (error) {
        console.error('Error loading user info:', error);
        setCurrentUser(null);
        setIsAdmin(false);
      }
    };
    
    loadUserInfo();
  }, []);

  // Separate useEffect for real-time stats auto-refresh
  useEffect(() => {
    // Set up auto-refresh for realtime stats every 15 seconds for better real-time experience
    const interval = setInterval(() => {
      if (tabValue === 0) {
        // Only refresh on overview tab
        console.log("Auto-refreshing realtime stats...");
        fetchRealtimeStats();
      }
    }, 15000); // Reduced to 15 seconds for better real-time experience

    return () => clearInterval(interval);
  }, [tabValue]); // Only depend on tabValue to avoid unnecessary interval recreation

  useEffect(() => {
    if (tabValue === 4) {
      // Chat Logs tab
      fetchChatLogs();
    }
  }, [
    tabValue,
    filters,
    chatFilters.page,
    chatFilters.pageSize,
    chatFilters.query,
  ]);

  const fetchAllAnalytics = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = buildFilterParams();

      // Fetch all analytics data in parallel
      const [
        overviewResponse,
        sentimentResponse,
        aiMetricsResponse,
        realtimeResponse,
        userEngagementResponse,
        intentStatsResponse,
      ] = await Promise.allSettled([
        analyticsApi.getAnalyticsSummary(params),
        analyticsApi.getSentimentAnalysis(params),
        analyticsApi.getAIMetrics(params),
        analyticsApi.getRealtimeStats(params),
        analyticsApi.getUserEngagement(params),
        analyticsApi.getIntentStatistics(filters.days),
      ]);

      // Handle responses with better error handling
      if (overviewResponse.status === "fulfilled") {
        setAnalytics(overviewResponse.value);
      } else {
        console.error("Error fetching overview:", overviewResponse.reason);
      }

      if (sentimentResponse.status === "fulfilled") {
        // Transform the API response to match the frontend expectations
        const apiData = sentimentResponse.value;
        const sentimentAnalysis = apiData.sentiment_analysis || {};

        // Transform to expected format
        const transformedData = {
          sentiment_distribution:
            sentimentAnalysis.sentiment_distribution || {},
          sentiment_trends: Object.entries(
            sentimentAnalysis.weekly_trends || {}
          ).map(([date, sentiments]) => ({
            date,
            positive: sentiments.positive || 0,
            neutral: sentiments.neutral || 0,
            negative: sentiments.negative || 0,
          })),
          total_messages_analyzed:
            sentimentAnalysis.total_messages_analyzed || 0,
          sentiment_percentages: sentimentAnalysis.sentiment_percentages || {},
          overall_sentiment_score: calculateOverallSentimentScore(
            sentimentAnalysis.sentiment_distribution || {}
          ),
          sentiment_trend: 0, // Calculate trend if needed
        };

        setSentimentData(transformedData);
      } else {
        console.error("Error fetching sentiment:", sentimentResponse.reason);
        setSentimentData({
          sentiment_distribution: {},
          sentiment_trends: [],
          total_messages_analyzed: 0,
          sentiment_percentages: {},
          overall_sentiment_score: 0,
          sentiment_trend: 0,
        });
      }

      if (aiMetricsResponse.status === "fulfilled") {
        setAiMetrics(aiMetricsResponse.value);
      } else {
        console.error("Error fetching AI metrics:", aiMetricsResponse.reason);
        setAiMetrics({
          total_ai_responses: 0,
          avg_response_time: 0,
          estimated_cost: 0,
        });
      }

      if (realtimeResponse.status === "fulfilled") {
        setRealtimeStats(realtimeResponse.value);
      } else {
        console.error(
          "Error fetching realtime stats:",
          realtimeResponse.reason
        );
        setRealtimeStats({
          realtime_metrics: {
            new_users_24h: 0,
            messages_last_hour: 0,
            active_conversations: 0,
            avg_response_time_last_hour: 0,
          },
          hourly_message_distribution: {},
        });
      }

      if (userEngagementResponse.status === "fulfilled") {
        setUserEngagement(userEngagementResponse.value);
      } else {
        console.error(
          "Error fetching user engagement:",
          userEngagementResponse.reason
        );
        setUserEngagement({
          active_users: 0,
          avg_session_duration: 0,
          retention_rate: 0,
          avg_messages_per_user: 0,
        });
      }

      if (intentStatsResponse.status === "fulfilled") {
        setIntentStats(intentStatsResponse.value);
      } else {
        console.error(
          "Error fetching intent stats:",
          intentStatsResponse.reason
        );
        setIntentStats([]);
      }
    } catch (error) {
      console.error("Error fetching analytics:", error);
      setError("Errore nel caricamento delle analytics. Riprova più tardi.");
    } finally {
      setLoading(false);
    }
  };

  const fetchChatLogs = async () => {
    try {
      setLoading(true);
      const params = {
        ...buildFilterParams(),
        page: chatFilters.page + 1,
        page_size: chatFilters.pageSize,
        query: chatFilters.query,
      };
      const data = await analyticsApi.getChatLogs(params);
      setChatLogs({
        logs: data.conversations || [],
        total: data.total_count || 0,
      });
    } catch (err) {
      console.error("Error fetching chat logs:", err);
      setError("Errore nel caricamento dei log chat.");
    } finally {
      setLoading(false);
    }
  };

  const handleSummaryUpdate = (conversationId, summary) => {
    setChatLogs((prev) => ({
      ...prev,
      logs: prev.logs.map((log) =>
        log.id === conversationId ? { ...log, summary: summary } : log
      ),
    }));
  };

  const fetchRealtimeStats = async () => {
    try {
      console.log("Fetching realtime stats...");
      const data = await analyticsApi.getRealtimeStats();
      console.log("Realtime stats updated:", data);
      setRealtimeStats(data);
    } catch (err) {
      console.error("Error fetching realtime stats:", err);
      // Don't clear the existing data on error, just log it
    }
  };

  const buildFilterParams = () => {
    const params = {};
    if (filters.platform) params.platform = filters.platform;
    if (filters.days) params.days = filters.days;
    if (filters.startDate) params.start_date = filters.startDate.toISOString();
    if (filters.endDate) params.end_date = filters.endDate.toISOString();
    return params;
  };

  const handleChatFilterChange = (field, value) => {
    setChatFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleViewMessages = (conversation) => {
    setSelectedConversation(conversation);
    setShowMessagesView(true);
  };

  const handleCloseMessagesView = () => {
    setShowMessagesView(false);
    setSelectedConversation(null);
  };

  const handleExportData = async () => {
    try {
      await analyticsApi.exportConversations("csv", buildFilterParams());
    } catch (error) {
      console.error("Error exporting data:", error);
    }
  };

  // Helper function to calculate overall sentiment score
  const calculateOverallSentimentScore = (sentimentDistribution) => {
    const total = Object.values(sentimentDistribution).reduce(
      (sum, count) => sum + count,
      0
    );
    if (total === 0) return 0;

    const weights = { positive: 1, neutral: 0, negative: -1 };
    const weightedSum = Object.entries(sentimentDistribution).reduce(
      (sum, [sentiment, count]) => {
        return sum + (weights[sentiment] || 0) * count;
      },
      0
    );

    return weightedSum / total;
  };

  if (loading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        p={4}
      >
        <CircularProgress />
        <Typography variant="body1" mt={2}>
          {t("dashboard.loadingAdvancedAnalytics")}
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        p={4}
      >
        <Alert severity="error">{error}</Alert>
        <Button variant="contained" onClick={fetchAllAnalytics} sx={{ mt: 2 }}>
          {t("common.retry", "Riprova")}
        </Button>
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box p={3}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={3}
        >
          <Typography variant="h4" gutterBottom>
            🚀 {t("app.title")}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <UserSettingsDropdown />
            <LanguageSelector variant="toggle" />
            <Tooltip title={t("dashboard.refreshData")}>
              <IconButton onClick={fetchAllAnalytics} color="primary">
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title={t("dashboard.exportData")}>
              <IconButton onClick={handleExportData} color="secondary">
                <DownloadIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>

        {/* Filters Section */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {t("dashboard.filtersAndPeriod")}
            </Typography>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth>
                  <InputLabel>{t("dashboard.platform")}</InputLabel>
                  <Select
                    value={filters.platform}
                    label={t("dashboard.platform")}
                    onChange={(e) =>
                      handleFilterChange("platform", e.target.value)
                    }
                  >
                    <MenuItem value="">{t("dashboard.allPlatforms")}</MenuItem>
                    <MenuItem value="telegram">
                      {t("dashboard.telegram")}
                    </MenuItem>
                    <MenuItem value="whatsapp">
                      {t("dashboard.whatsapp")}
                    </MenuItem>
                    <MenuItem value="instagram">
                      {t("dashboard.instagram")}
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth>
                  <InputLabel>{t("dashboard.period")}</InputLabel>
                  <Select
                    value={filters.days}
                    label={t("dashboard.period")}
                    onChange={(e) => handleFilterChange("days", e.target.value)}
                  >
                    <MenuItem value={7}>{t("dashboard.last7Days")}</MenuItem>
                    <MenuItem value={30}>{t("dashboard.last30Days")}</MenuItem>
                    <MenuItem value={90}>{t("dashboard.last90Days")}</MenuItem>
                    <MenuItem value={365}>
                      {t("common.lastYear", "Ultimo anno")}
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <DatePicker
                  label={t("dashboard.startDate")}
                  value={filters.startDate}
                  onChange={(date) => handleFilterChange("startDate", date)}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <DatePicker
                  label={t("dashboard.endDate")}
                  value={filters.endDate}
                  onChange={(date) => handleFilterChange("endDate", date)}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
              </Grid>
            </Grid>
            <Box mt={2}>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {filters.platform && (
                  <Chip
                    label={`Piattaforma: ${filters.platform}`}
                    onDelete={() => handleFilterChange("platform", "")}
                    size="small"
                  />
                )}
                {filters.days !== 30 && (
                  <Chip
                    label={`Periodo: ${filters.days} giorni`}
                    onDelete={() => handleFilterChange("days", 30)}
                    size="small"
                  />
                )}
                {filters.startDate && (
                  <Chip
                    label={`Da: ${filters.startDate.toLocaleDateString()}`}
                    onDelete={() => handleFilterChange("startDate", null)}
                    size="small"
                  />
                )}
                {filters.endDate && (
                  <Chip
                    label={`A: ${filters.endDate.toLocaleDateString()}`}
                    onDelete={() => handleFilterChange("endDate", null)}
                    size="small"
                  />
                )}
              </Stack>
            </Box>
          </CardContent>
        </Card>

        {/* Realtime Stats Banner */}
        {realtimeStats && (
          <Card
            sx={{
              mb: 3,
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              color: "white",
            }}
          >
            <CardContent>
              <Typography
                variant="h6"
                gutterBottom
                sx={{ display: "flex", alignItems: "center", gap: 1 }}
              >
                ⚡ {t("metrics.realtimeStats")}
                <Chip
                  size="small"
                  label="LIVE"
                  sx={{
                    backgroundColor: "#4caf50",
                    color: "white",
                    animation: "pulse 2s infinite",
                  }}
                />
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={4}>
                  <Box textAlign="center">
                    <Typography variant="h4" sx={{ fontWeight: "bold" }}>
                      {realtimeStats.realtime_metrics?.new_users_24h || 0}
                    </Typography>
                    <Typography variant="body2">
                      {t("metrics.newUsers24h")}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Box textAlign="center">
                    <Typography variant="h4" sx={{ fontWeight: "bold" }}>
                      {realtimeStats.realtime_metrics?.messages_last_hour || 0}
                    </Typography>
                    <Typography variant="body2">
                      {t("metrics.messagesLastHour")}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Box textAlign="center">
                    <Typography variant="h4" sx={{ fontWeight: "bold" }}>
                      {realtimeStats.realtime_metrics?.active_conversations ||
                        0}
                    </Typography>
                    <Typography variant="body2">
                      {t("metrics.activeConversations")}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        )}

        {/* Main Analytics Tabs */}
        <Paper sx={{ mb: 3 }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            indicatorColor="primary"
            textColor="primary"
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label={t("navigation.overview")} />
            <Tab label={t("navigation.sentiment")} />
            <Tab label={t("navigation.aiMetrics")} />
            <Tab label={t("navigation.userEngagement")} />
            <Tab label={t("navigation.chatLogs")} />
            <Tab label={t("navigation.advancedAnalytics")} />
            {isAdmin && <Tab label="Knowledge Base" />}
            {isAdmin && <Tab label="👥 User Logs" />}
            {isAdmin && <Tab label="🔄 Follow-up Service" />}
            {isAdmin && <Tab label="📊 Intent Monitor" />}
          </Tabs>
        </Paper>

        {/* Tab Content */}
        {tabValue === 0 && (
          <Grid container spacing={3}>
            {/* Overview Analytics Cards */}
            <Grid item xs={12}>
              <AnalyticsCards data={analytics || {}} />
            </Grid>
            {/* Intent Summary Card */}
            <Grid item xs={12} md={6}>
              <IntentSummaryCard data={intentStats} />
            </Grid>
            {/* AI Metrics Quick View */}
            <Grid item xs={12} md={6}>
              <AIMetricsCard data={aiMetrics} />
            </Grid>
            {/* Realtime Stats Component */}
            <Grid item xs={12}>
              <RealtimeStats
                data={realtimeStats}
                onRefresh={fetchRealtimeStats}
              />
            </Grid>
          </Grid>
        )}

        {tabValue === 1 && (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <SentimentChart data={sentimentData} detailed={true} />
            </Grid>
            {sentimentData && (
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Distribuzione Sentiment
                    </Typography>
                    <Grid container spacing={2}>
                      {Object.entries(
                        sentimentData.sentiment_distribution || {}
                      ).map(([sentiment, count]) => (
                        <Grid item xs={12} sm={4} key={sentiment}>
                          <Box textAlign="center" p={2}>
                            <Typography variant="h4" color="primary">
                              {count}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                              {sentiment.charAt(0).toUpperCase() +
                                sentiment.slice(1)}
                            </Typography>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            )}
          </Grid>
        )}

        {tabValue === 2 && (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <AIMetricsCard data={aiMetrics} detailed={true} />
            </Grid>
            {aiMetrics && (
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Dettagli Performance AI
                    </Typography>
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={4}>
                        <Box textAlign="center" p={2}>
                          <Typography variant="h4" color="primary">
                            {aiMetrics.total_ai_responses || 0}
                          </Typography>
                          <Typography variant="body2">
                            Risposte Generate
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Box textAlign="center" p={2}>
                          <Typography variant="h4" color="secondary">
                            {(aiMetrics.avg_response_time || 0).toFixed(2)}s
                          </Typography>
                          <Typography variant="body2">
                            Tempo Medio Risposta
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Box textAlign="center" p={2}>
                          <Typography variant="h4" color="success.main">
                            ${(aiMetrics.estimated_cost || 0).toFixed(4)}
                          </Typography>
                          <Typography variant="body2">Costo Stimato</Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            )}
          </Grid>
        )}

        {tabValue === 3 && (
          <Grid container spacing={3}>
            {userEngagement ? (
              <>
                <Grid item xs={12}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Coinvolgimento Utenti
                      </Typography>
                      <Grid container spacing={3}>
                        <Grid item xs={12} md={3}>
                          <Box textAlign="center" p={2}>
                            <Typography variant="h4" color="primary">
                              {userEngagement.active_users || 0}
                            </Typography>
                            <Typography variant="body2">
                              Utenti Attivi
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <Box textAlign="center" p={2}>
                            <Typography variant="h4" color="secondary">
                              {(
                                userEngagement.avg_session_duration || 0
                              ).toFixed(1)}
                              min
                            </Typography>
                            <Typography variant="body2">
                              Durata Media Sessione
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <Box textAlign="center" p={2}>
                            <Typography variant="h4" color="success.main">
                              {(userEngagement.retention_rate || 0).toFixed(1)}%
                            </Typography>
                            <Typography variant="body2">
                              Tasso di Retention
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <Box textAlign="center" p={2}>
                            <Typography variant="h4" color="warning.main">
                              {(
                                userEngagement.avg_messages_per_user || 0
                              ).toFixed(1)}
                            </Typography>
                            <Typography variant="body2">
                              Messaggi per Utente
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              </>
            ) : (
              <Grid item xs={12}>
                <Alert severity="info">
                  Dati di engagement utenti non disponibili. Implementazione in
                  corso.
                </Alert>
              </Grid>
            )}
          </Grid>
        )}

        {tabValue === 4 && (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              {showMessagesView && selectedConversation ? (
                <MessagesView
                  conversationId={selectedConversation.id}
                  conversationTitle={selectedConversation.title}
                  onClose={handleCloseMessagesView}
                />
              ) : (
                <ChatTable
                  logs={chatLogs.logs}
                  total={chatLogs.total}
                  loading={loading}
                  filters={chatFilters}
                  onFilterChange={handleChatFilterChange}
                  onViewMessages={handleViewMessages}
                  onSummaryUpdate={handleSummaryUpdate}
                />
              )}
            </Grid>
          </Grid>
        )}

        {tabValue === 5 && (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <AdvancedAnalytics filters={filters} />
            </Grid>
          </Grid>
        )}

        {/* Knowledge Base Tab - Admin Only */}
        {isAdmin && tabValue === 6 && (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <KnowledgeBaseSettings />
            </Grid>
          </Grid>
        )}

        {/* User Logs Tab - Admin Only */}
        {isAdmin && tabValue === 7 && (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <UserLogs />
            </Grid>
          </Grid>
        )}

        {/* Follow-up Service Tab - Admin Only */}
        {isAdmin && tabValue === 8 && (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FollowUpMonitor />
            </Grid>
          </Grid>
        )}

        {/* Intent Monitor Tab - Admin Only */}
        {isAdmin && tabValue === 9 && (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <IntentMonitor />
            </Grid>
          </Grid>
        )}
      </Box>
    </LocalizationProvider>
  );
}

export default Dashboard;
